import { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Button } from './ui/button';
import { EXPERT_AVATAR_DIMENSIONS } from '../utils/expertProfileImageUtils';

interface CroppedAreaPixels {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface ExpertAvatarCropperProps {
  imageSrc: string;
  onCropComplete: (croppedAreaPixels: CroppedAreaPixels) => void;
  onCancel: () => void;
}

export default function ExpertAvatarCropper({
  imageSrc,
  onCropComplete,
  onCancel,
}: ExpertAvatarCropperProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CroppedAreaPixels | null>(null);

  const onCropChange = useCallback((location: { x: number; y: number }) => {
    setCrop(location);
  }, []);

  const onZoomChange = useCallback((z: number) => {
    setZoom(z);
  }, []);

  const handleCropComplete = useCallback(
    (_croppedArea: unknown, croppedAreaPx: CroppedAreaPixels) => {
      setCroppedAreaPixels(croppedAreaPx);
    },
    []
  );

  const handleConfirm = useCallback(() => {
    if (croppedAreaPixels) {
      onCropComplete(croppedAreaPixels);
    }
  }, [croppedAreaPixels, onCropComplete]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black">
      {/* Cropper Area */}
      <div className="relative flex-1">
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          aspect={EXPERT_AVATAR_DIMENSIONS.aspectRatio}
          cropShape="round"
          onCropChange={onCropChange}
          onZoomChange={onZoomChange}
          onCropComplete={handleCropComplete}
        />
      </div>

      {/* Controls */}
      <div className="bg-white p-4 space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Zoom</label>
          <input
            type="range"
            min={1}
            max={3}
            step={0.1}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="w-full"
          />
        </div>

        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
            Cancel
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={!croppedAreaPixels} className="flex-1">
            Confirm
          </Button>
        </div>
      </div>
    </div>
  );
}
